import { useState, useMemo } from "react";
import { useOutletContext, Outlet, Link } from "react-router-dom";
import { Typography, Badge, Input, Card, CardContent, Button, Stack } from "@altimateai/lego";
import { IconSearch, IconAlertTriangle } from "@tabler/icons-react";
import { DataTable } from "@/components/tables";
import { TableHeaderType, TableRowItem, FilterType } from "@/components/tables/types";
import { Learning } from "./types";
import { useQuery } from "@tanstack/react-query";
import { getKnowledgeBaseDocumentChunks } from "@/helpers/apis";
import { ColorInsightTags } from "@/components/tags";
import { RelativeComponentLoader } from "@/components/loader";
import {
  NoDataAvailableState,
  DocumentProcessingState,
  NoResultsState,
} from "./components/emptyStates";
import { transformChunksToLearnings } from "./utils/learningsTransform";
import { isFailedStatus, toUserFriendlyError } from "../utils/statusHelpers";
import { KnowledgeBaseDetailResponse } from "../types";

interface LearningsListProps {
  kbId?: number;
  documentId?: number;
}

interface OutletContext {
  data?: KnowledgeBaseDetailResponse;
  kbId?: number;
  documentId?: number;
}

export default function LearningsList(props?: LearningsListProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const [selectedDocumentFilters, setSelectedDocumentFilters] = useState<string[]>([]);

  // Support both direct props and outlet context
  const contextData = useOutletContext<OutletContext | undefined>();
  const kbId = props?.kbId ?? contextData?.kbId;
  const documentId = props?.documentId ?? contextData?.documentId;
  const isFailed = isFailedStatus(contextData?.data?.processing_status);
  // Fetch learnings from the API
  const { data: chunksData, isLoading } = useQuery({
    queryKey: ["knowledge-base-chunks-learnings", kbId, documentId],
    queryFn: () => getKnowledgeBaseDocumentChunks(kbId!, documentId!),
    enabled: !!kbId && !!documentId,
    gcTime: 5 * 60 * 1000,
  });

  // Transform API data to Learning objects
  const learnings = useMemo<Learning[]>(() => transformChunksToLearnings(chunksData), [chunksData]);

  // Get unique tags and documents for filters
  const uniqueTags = useMemo(() => {
    const tags = new Set<string>();
    learnings.forEach(learning => {
      learning.tags?.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).map(tag => ({ label: tag, value: tag }));
  }, [learnings]);

  const uniqueDocuments = useMemo(() => {
    const documents = new Map<string, number>();
    learnings.forEach(learning => {
      documents.set(learning.document_name, learning.document_id);
    });
    return Array.from(documents.entries()).map(([name, id]) => ({
      label: name,
      value: id.toString(),
    }));
  }, [learnings]);

  // Filter learnings based on search and filters
  const filteredLearnings = useMemo(() => {
    return learnings.filter(learning => {
      const matchesSearch =
        searchQuery === "" ||
        learning.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        learning.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        learning.content.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesTags =
        selectedTagFilters.length === 0 ||
        learning.tags?.some(tag => selectedTagFilters.includes(tag));

      const matchesDocuments =
        selectedDocumentFilters.length === 0 ||
        selectedDocumentFilters.includes(learning.document_id.toString());

      return matchesSearch && matchesTags && matchesDocuments;
    });
  }, [learnings, searchQuery, selectedTagFilters, selectedDocumentFilters]);

  const handleTagFilterChange = (value: unknown) => {
    setSelectedTagFilters(value as string[]);
  };

  const handleDocumentFilterChange = (value: unknown) => {
    setSelectedDocumentFilters(value as string[]);
  };

  const header: TableHeaderType<Learning>[] = [
    {
      id: 1,
      uid: "name",
      label: "Learning Name",
      width: "30%",
      render: (item: Learning) => (
        <Typography variant="body" weight="medium" className="al-line-clamp-1">
          {item.name}
        </Typography>
      ),
    },
    {
      id: 2,
      uid: "knowledge_type",
      label: "Type",
      width: "10%",
      render: (item: Learning) => (
        <>
          {item.knowledge_type ? (
            <Badge
              variant="outline"
              className="al-bg-blue-50 al-text-blue-600 al-border-blue-200 al-text-xs al-capitalize"
            >
              {item.knowledge_type}
            </Badge>
          ) : (
            <Typography variant="body" size="sm" className="al-text-gray-400">
              -
            </Typography>
          )}
        </>
      ),
    },
    {
      id: 3,
      uid: "tags",
      label: "Tags",
      width: "25%",
      render: (item: Learning) => (
        <>
          {item.tags && item.tags.length > 0 ? (
            <ColorInsightTags tags={item.tags} showMore />
          ) : (
            <Typography variant="body" size="sm" className="al-text-gray-400">
              -
            </Typography>
          )}
        </>
      ),
      filter: !documentId
        ? {
            filterType: FilterType.DROPDOWN,
            onChange: handleTagFilterChange,
            options: uniqueTags,
            value: selectedTagFilters,
            id: "tags",
            label: "Tags",
          }
        : undefined,
    },
    {
      id: 4,
      uid: "document_name",
      label: "Document",
      width: "20%",
      render: (item: Learning) => (
        <Typography variant="body" size="sm" className="al-text-gray-700 al-line-clamp-1">
          {item.document_name}
        </Typography>
      ),
      filter: !documentId
        ? {
            filterType: FilterType.DROPDOWN,
            onChange: handleDocumentFilterChange,
            options: uniqueDocuments,
            value: selectedDocumentFilters,
            id: "document",
            label: "Documents",
          }
        : undefined,
    },
    {
      id: 5,
      uid: "retrievals",
      label: "Retrievals",
      width: "15%",
      render: (item: Learning) => (
        <Stack direction="row" align="center" gap="xs">
          <Typography variant="body" size="sm" weight="medium" className="al-text-gray-900">
            {item.retrievals ?? 0}
          </Typography>
          <Typography variant="body" size="sm" className="al-text-gray-500">
            times
          </Typography>
        </Stack>
      ),
    },
  ];

  if (isLoading) {
    return <RelativeComponentLoader label={"Loading learnings..."} componentHeight={20} />;
  }

  if (!kbId || !documentId) {
    return <NoDataAvailableState />;
  }

  // Show failure state before checking for learnings
  if (isFailed) {
    return (
      <Card className="al-border al-border-gray-200">
        <CardContent className="al-py-20">
          <Stack direction="column" align="center" justify="center" gap="lg">
            <div className="al-rounded-full al-bg-red-100 al-p-6">
              <IconAlertTriangle className="al-w-10 al-h-10 al-text-red-600" />
            </div>
            <Stack direction="column" align="center" gap="sm">
              <Typography variant="h3" weight="semibold" className="al-text-gray-900">
                Document processing failed
              </Typography>
              <Typography
                variant="body"
                size="sm"
                className="al-text-gray-600 al-text-center al-max-w-lg"
              >
                {toUserFriendlyError(contextData?.data?.processing_error)}
              </Typography>
              <Button asChild variant="outline" className="al-mt-2">
                <Link to="../history">View History</Link>
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  if (learnings.length === 0) {
    return <DocumentProcessingState />;
  }

  return (
    <>
      <Stack direction="column" gap="md">
        <div className="al-relative al-max-w-md">
          <Input
            type="search"
            placeholder="Search for a learning"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="al-pl-9"
          />
          <IconSearch className="al-absolute al-left-3 al-top-1/2 -al-translate-y-1/2 al-h-4 al-w-4 al-text-gray-500 al-pointer-events-none" />
        </div>

        {filteredLearnings.length > 0 ? (
          <DataTable
            header={header}
            data={{ items: filteredLearnings }}
            getRowUrl={(item: TableRowItem) =>
              `/knowledge-hub/${kbId}/learnings/${(item as Learning).id}`
            }
            getRowState={(item: TableRowItem) => ({ entityName: (item as Learning).name })}
          />
        ) : (
          <NoResultsState />
        )}
      </Stack>

      {/* Outlet for nested routes - passes learnings data to LearningDetailsSidebar */}
      <Outlet context={{ learnings }} />
    </>
  );
}
